/* ============================================================
   ROOK — Theme JavaScript
   ============================================================ */

'use strict';

/* --- Helpers --------------------------------------------- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

/* --- Dial colour worlds -----------------------------------
   CSS classes (dial-world--*) already define all --pdp-* and --dial-* custom
   properties via theme.css, picking up merchant Theme Editor colour overrides
   from the inline <style> block in theme.liquid. JS simply swaps the class —
   no hardcoded hex values needed, so merchant customisations are fully respected.
   --------------------------------------------------------- */
const DIAL_CLASS_MAP = {
  'white jade':        'dial-world--white-jade',
  'dark green marble': 'dial-world--dark-green-marble',
  'green marble':      'dial-world--dark-green-marble',
  'green stone':       'dial-world--dark-green-marble',
  'tentite marble':    'dial-world--tentite-marble',
  'tentite':           'dial-world--tentite-marble',
  'orange stone':      'dial-world--tentite-marble',
  'black agate':       'dial-world--black-agate',
  'black stone':       'dial-world--black-agate',
};
const ALL_DIAL_CLASSES = Object.values(DIAL_CLASS_MAP);

function getDialClass(title) {
  if (!title) return null;
  return DIAL_CLASS_MAP[title.toLowerCase().trim()] || null;
}

function applyDialClass(dialClass, el) {
  if (!dialClass || !el) return;
  el.classList.remove(...ALL_DIAL_CLASSES);
  el.classList.add(dialClass);
}

/* --- Header scroll effect -------------------------------- */
function initHeader() {
  const header = $('.site-header');
  if (!header) return;

  const update = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 40);
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
}

/* --- Mobile menu ----------------------------------------- */
function initMobileMenu() {
  const toggle = $('.menu-toggle');
  const nav    = $('.mobile-nav');
  if (!toggle || !nav) return;

  const open  = () => { toggle.classList.add('is-open'); nav.classList.add('is-open'); document.body.style.overflow = 'hidden'; };
  const close = () => { toggle.classList.remove('is-open'); nav.classList.remove('is-open'); document.body.style.overflow = ''; };

  toggle.addEventListener('click', () => toggle.classList.contains('is-open') ? close() : open());
  $$('a', nav).forEach(a => a.addEventListener('click', close));
}

/* --- Hero animate-in ------------------------------------- */
function initHero() {
  const hero = $('.hero');
  if (!hero) return;
  requestAnimationFrame(() => hero.classList.add('is-loaded'));
}

/* --- Scroll reveal --------------------------------------- */
function revealAll(ctx = document) {
  $$(`.reveal`, ctx).forEach(el => el.classList.add('is-visible'));
}

function initReveal() {
  // In the Shopify theme editor, sections are already on screen and scrolling
  // is not simulated — skip the observer and show everything immediately.
  if (window.Shopify && window.Shopify.designMode) {
    revealAll();
    return;
  }

  if (!('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('is-visible');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.12 });

  $$('.reveal').forEach(el => io.observe(el));
}

// When the theme editor reloads a section after a settings change, re-reveal
// any new .reveal elements that were injected into that section.
document.addEventListener('shopify:section:load', (e) => {
  if (window.Shopify && window.Shopify.designMode) {
    revealAll(e.target);
  }
});

/* --- Cart drawer ----------------------------------------- */
let cartDrawer, cartOverlay, cartBody, cartCount;

function initCartDrawer() {
  cartDrawer  = $('.cart-drawer');
  cartOverlay = $('#siteOverlay');
  cartBody    = $('.cart-drawer__body');
  cartCount   = $$('[data-cart-count]');

  if (!cartDrawer) return;

  $$('[data-cart-open]').forEach(btn => btn.addEventListener('click', openCart));
  $$('[data-cart-close]').forEach(btn => btn.addEventListener('click', closeCart));
  cartOverlay?.addEventListener('click', closeCart);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeCart();
  });
}

function openCart() {
  cartDrawer?.classList.add('is-open');
  cartOverlay?.classList.add('is-active');
  document.body.style.overflow = 'hidden';
  refreshCartDrawer();
}

function closeCart() {
  cartDrawer?.classList.remove('is-open');
  cartOverlay?.classList.remove('is-active');
  document.body.style.overflow = '';
}

async function refreshCartDrawer() {
  if (!cartDrawer) return;
  try {
    const res  = await fetch('/cart.js');
    const data = await res.json();
    renderCartDrawer(data);
    updateCartCount(data.item_count);
  } catch (err) {
    console.error('Cart refresh failed', err);
  }
}

function updateCartCount(count) {
  cartCount?.forEach(el => {
    el.textContent = count;
    el.dataset.count = count;
  });
}

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const fmt = (window.Rook && window.Rook.moneyFormat) || '{{amount}}';
  return fmt.replace(/\{\{\s*amount[^}]*\}\}/g, amount);
}

function renderCartDrawer(cart) {
  if (!cartBody) return;
  if (!cart.item_count) {
    cartBody.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
    const amt = $('.cart-subtotal__amount');
    if (amt) amt.textContent = formatMoney(0);
    return;
  }

  const items = cart.items.map(item => `
    <div class="cart-item" data-key="${item.key}">
      <div class="cart-item__image">
        ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
      </div>
      <div class="cart-item__info">
        <div class="cart-item__title">${item.product_title}</div>
        <div class="cart-item__variant">${item.variant_title || ''}</div>
        <div class="cart-item__price">${formatMoney(item.line_price)}</div>
        <button class="cart-item__remove" data-key="${item.key}">Remove</button>
      </div>
    </div>
  `).join('');

  cartBody.innerHTML = items;

  const amt = $('.cart-subtotal__amount');
  if (amt) amt.textContent = formatMoney(cart.total_price);

  $$('.cart-item__remove', cartBody).forEach(btn => {
    btn.addEventListener('click', () => removeCartItem(btn.dataset.key));
  });
}

async function removeCartItem(key) {
  try {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    await refreshCartDrawer();
  } catch (err) {
    console.error('Remove item failed', err);
  }
}

/* --- Add to cart ----------------------------------------- */
function initAddToCart() {
  const form = $('[data-atc-form]');
  if (!form) return;

  form.addEventListener('submit', async e => {
    e.preventDefault();
    const btn = $('[data-atc-btn]', form);
    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span>';

    try {
      const formData = new FormData(form);
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id:       formData.get('id'),
          quantity: parseInt(formData.get('quantity') || 1, 10),
        }),
      });
      await refreshCartDrawer();
      openCart();
    } catch (err) {
      console.error('Add to cart failed', err);
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  });
}

/* --- Variant selector ------------------------------------ */
function initVariantSelector() {
  const selector = $('[data-variant-selector]');
  if (!selector) return;

  const options = $$('.variant-option', selector);
  const variantInput = $('[name="id"]');
  const priceEl = $('.product-info__price');

  // Build variant map from data attr
  let variantData = [];
  try {
    variantData = JSON.parse(selector.dataset.variants || '[]');
  } catch {}

  function selectOption(btn) {
    options.forEach(o => o.classList.remove('is-active'));
    btn.classList.add('is-active');

    const title   = btn.dataset.optionValue || '';
    const variant = variantData.find(v =>
      v.option1?.toLowerCase() === title.toLowerCase() ||
      v.title?.toLowerCase() === title.toLowerCase()
    );

    if (variant) {
      if (variantInput) variantInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
    }

    // Apply dial colour world — swap CSS class so merchant Theme Editor
    // colour overrides (set via inline <style> in theme.liquid) are respected
    const dialClass = getDialClass(title);
    const productPage = $('#productPage');
    if (dialClass && productPage) applyDialClass(dialClass, productPage);

    // Swap gallery image if variant has featured_image
    if (variant?.featured_image?.src) {
      const mainImg = $('.product-gallery__main img');
      if (mainImg) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = variant.featured_image.src;
          mainImg.style.opacity = '1';
        }, 180);
      }
    }
  }

  options.forEach(btn => {
    btn.addEventListener('click', () => selectOption(btn));
  });

  // Select first by default
  if (options[0]) selectOption(options[0]);
}

/* --- Gallery thumbnails ---------------------------------- */
function initGallery() {
  const thumbs  = $$('.product-gallery__thumb');
  const mainImg = $('.product-gallery__main img');
  if (!thumbs.length || !mainImg) return;

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      const src = thumb.dataset.src;
      if (src) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = src;
          mainImg.style.opacity = '1';
        }, 180);
      }
    });
  });
}

/* --- Quantity input -------------------------------------- */
function initQuantity() {
  const wrap = $('.quantity-input');
  if (!wrap) return;

  const input = $('input[name="quantity"]', wrap);
  const dec   = $('[data-qty-dec]', wrap);
  const inc   = $('[data-qty-inc]', wrap);

  dec?.addEventListener('click', () => {
    const val = parseInt(input.value, 10);
    if (val > 1) input.value = val - 1;
  });
  inc?.addEventListener('click', () => {
    input.value = parseInt(input.value, 10) + 1;
  });
}

/* --- Product detail accordions --------------------------- */
function initAccordions() {
  $$('.product-detail-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const body = btn.nextElementSibling;
      const isOpen = btn.classList.contains('is-open');
      btn.classList.toggle('is-open', !isOpen);
      body?.classList.toggle('is-open', !isOpen);
    });
  });
}

/* --- Boot ----------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileMenu();
  initHero();
  initReveal();
  initCartDrawer();
  initAddToCart();
  initVariantSelector();
  initGallery();
  initQuantity();
  initAccordions();
});
